import type { BuildingType, ResolvedBuilding } from '$lib/types'

const KNOWN_TYPES = new Set<BuildingType>([
  'residential',
  'production',
  'goods',
  'culture',
  'decoration',
  'military',
  'street',
  'greatbuilding',
  'main_building',
  'diplomacy',
  'random_production',
  'cultural_goods_production',
  'static_provider',
  'tower',
  'placement',
  'off_grid',
  'outpost_ship',
  'friends_tavern',
  'hub_main',
  'hub_part',
  'impediment',
  'clan_power_production',
  'unknown',
])

const OFF_GRID_TYPES = new Set<BuildingType>([
  'friends_tavern',
  'outpost_ship',
  'off_grid',
  'hub_main',
  'hub_part',
])

interface CatalogueLike {
  id?: string
  name?: string
  type?: string
  width?: number
  length?: number
  height?: number
  era?: string
  components?: {
    AllAge?: {
      placement?: {
        size?: { x?: number; y?: number }
      }
      /**
       * Newer multi-state buildings encode their road requirement here as a
       * camelCase object instead of the older snake_case top-level field.
       * Shape: `{ type: 'streetConnectionRequirement', requiredLevel: 1 }`.
       * Found on event/raid/expedition/cultural-settlement buildings — 743
       * of them in the bundled reference export.
       */
      streetConnectionRequirement?: {
        requiredLevel?: number
      }
    }
  }
  requirements?: {
    street_connection_level?: number
  }
}

/**
 * Size lives in two places depending on the entity. See docs/data-formats.md
 * "the size dual-path quirk" — older entities use top-level width/length,
 * newer/multi-state ones tuck size under components.AllAge.placement.size.
 */
function resolveSize(entity: CatalogueLike): { width: number; height: number } {
  const w = entity.width ?? entity.components?.AllAge?.placement?.size?.x ?? 0
  const h =
    entity.height ?? entity.length ?? entity.components?.AllAge?.placement?.size?.y ?? 0
  return { width: w, height: h }
}

function classifyType(raw: string | undefined): BuildingType {
  if (!raw) return 'unknown'
  return KNOWN_TYPES.has(raw as BuildingType) ? (raw as BuildingType) : 'unknown'
}

/**
 * Turn a CityEntities-shaped record (from either an import or the reference
 * catalogue) into our internal ResolvedBuilding. Keeps the original record
 * accessible via `raw` for stats/production work in later phases.
 */
export function normalize(id: string, entity: unknown): ResolvedBuilding {
  const e = (entity ?? {}) as CatalogueLike
  const { width, height } = resolveSize(e)
  const type = classifyType(e.type)
  // Two mutually-exclusive catalogue formats in real player exports:
  //   - older: `requirements.street_connection_level` (snake_case, top-level)
  //   - newer: `components.AllAge.streetConnectionRequirement.requiredLevel`
  //     (camelCase, under components — used by event / raid / expedition /
  //     cultural-settlement buildings). The reference export has 1031 of the
  //     first format, 743 of the second, and zero overlap. Reading only the
  //     top-level path silently misses every road-needing event residential
  //     and production — see the Phase 3 manual-smoke bug retro.
  const topLevelLevel = e.requirements?.street_connection_level ?? 0
  const componentsLevel =
    e.components?.AllAge?.streetConnectionRequirement?.requiredLevel ?? 0
  const needsRoad = topLevelLevel > 0 || componentsLevel > 0
  const isRoad = type === 'street'
  const isMainBuilding = type === 'main_building'
  const isOffGrid = OFF_GRID_TYPES.has(type)

  return {
    id,
    name: typeof e.name === 'string' ? e.name : id,
    width,
    height,
    type,
    era: typeof e.era === 'string' ? e.era : undefined,
    needsRoad,
    isRoad,
    isMainBuilding,
    isOffGrid,
    raw: entity,
  }
}
